import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Mail, MapPin, Phone, RotateCcw } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Full-Service Vending",
  "Micro Market",
  "Coffee & Pantry",
  "Smart Coolers",
  "Not sure yet",
];

const EMPLOYEES = ["1–25", "26–75", "76–200", "200+"];

const INITIAL = {
  company_name: "",
  contact_name: "",
  email: "",
  phone: "",
  employees: "26–75",
  service_type: "Full-Service Vending",
  message: "",
};

export default function QuoteForm() {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim() || !form.contact_name.trim() || !form.email.trim()) {
      toast.error("Please fill in your company, name and email.");
      return;
    }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/quotes`, form);
      setDone(data);
      toast.success("Quote request received — we'll be in touch within 1–3 business days.");
    } catch (err) {
      toast.error("Something went wrong sending your request. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setForm(INITIAL);
    setDone(null);
  };

  return (
    <section id="quote" data-testid="quote-request-section" className="relative py-24 lg:py-32">
      <div
        aria-hidden="true"
        className="absolute right-[-8%] top-1/4 h-[440px] w-[440px] rounded-full bg-[#1B365D]/10 blur-[130px]"
      />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-14 px-5 sm:px-8 lg:grid-cols-2 lg:gap-16">
        <div>
          <Reveal>
            <Eyebrow>Free quote</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-[#16120E] sm:text-4xl lg:text-5xl">
              Your breakroom upgrade starts here.
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-stone-600 sm:text-lg">
              Tell us about your space and we'll design a snack program for it —
              equipment, install and 24/7 management included, at no cost to you.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <ul className="mt-8 space-y-3.5">
              {[
                "Free equipment & installation",
                "Custom product menu for your team",
                "Same-day service guarantee",
              ].map((b) => (
                <li key={b} className="flex items-center gap-3 text-sm text-stone-700 sm:text-base">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#A8861D]/12">
                    <CheckCircle2 className="h-3.5 w-3.5 text-[#A8861D]" />
                  </span>
                  {b}
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-10 space-y-4 border-t border-black/10 pt-8">
              <a
                data-testid="footer-support-phone"
                href="tel:+12042186681"
                className="flex items-center gap-4 text-[#16120E] transition-colors hover:text-[#1B365D]"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-black/10 bg-white">
                  <Phone className="h-5 w-5 text-[#1B365D]" />
                </span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                    24/7 support line
                  </span>
                  <span className="font-semibold">204-218-6681</span>
                </span>
              </a>
              <a
                href="mailto:info@snacksavvyvending.ca"
                className="flex items-center gap-4 text-[#16120E] transition-colors hover:text-[#1B365D]"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-black/10 bg-white">
                  <Mail className="h-5 w-5 text-[#1B365D]" />
                </span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                    Email us
                  </span>
                  <span className="font-semibold">info@snacksavvyvending.ca</span>
                </span>
              </a>
              <div className="flex items-center gap-4 text-[#16120E]">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-black/10 bg-white">
                  <MapPin className="h-5 w-5 text-[#1B365D]" />
                </span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                    Service area
                  </span>
                  <span className="font-semibold">Winnipeg & Manitoba-wide</span>
                </span>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="relative overflow-hidden rounded-3xl border border-black/10 bg-white p-7 shadow-[0_40px_120px_rgba(27,54,93,0.08)] sm:p-10">
            <div
              aria-hidden="true"
              className="absolute -right-20 -top-20 h-56 w-56 rounded-full bg-[#C9A227]/25 blur-[80px]"
            />
            {done ? (
              <motion.div
                data-testid="quote-success-message"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="relative flex min-h-[430px] flex-col items-center justify-center text-center"
              >
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#C9A227]/15">
                  <CheckCircle2 className="h-8 w-8 text-[#A8861D]" />
                </span>
                <h3 className="mt-6 font-display text-2xl font-extrabold text-[#16120E]">
                  Request received!
                </h3>
                <p className="mt-3 max-w-xs text-sm leading-relaxed text-stone-600">
                  Thanks, {form.contact_name.split(" ")[0] || "there"} — your reference is{" "}
                  <span className="font-mono text-[#1B365D]">
                    #{String(done._id || done.id).slice(-6).toUpperCase()}
                  </span>
                  . A SnackSavvy specialist will reach out within 1–3 business days.
                </p>
                <button
                  onClick={reset}
                  className="mt-8 inline-flex items-center gap-2 rounded-full border border-black/15 px-6 py-3 text-sm font-semibold text-[#16120E] transition-all hover:-translate-y-0.5 hover:border-black/40"
                >
                  <RotateCcw className="h-4 w-4" /> Send another request
                </button>
              </motion.div>
            ) : (
              <form
                data-testid="quote-request-form"
                onSubmit={submit}
                className="relative space-y-5"
                noValidate
              >
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="q-company" className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">
                      Company *
                    </label>
                    <input
                      id="q-company"
                      data-testid="quote-input-company-name"
                      className="field"
                      placeholder="Acme Inc."
                      value={form.company_name}
                      onChange={set("company_name")}
                    />
                  </div>
                  <div>
                    <label htmlFor="q-name" className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">
                      Your name *
                    </label>
                    <input
                      id="q-name"
                      data-testid="quote-input-contact-name"
                      className="field"
                      placeholder="Jamie Lee"
                      value={form.contact_name}
                      onChange={set("contact_name")}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="q-email" className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">
                      Work email *
                    </label>
                    <input
                      id="q-email"
                      data-testid="quote-input-email"
                      type="email"
                      className="field"
                      placeholder="jamie@acme.com"
                      value={form.email}
                      onChange={set("email")}
                    />
                  </div>
                  <div>
                    <label htmlFor="q-phone" className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">
                      Phone
                    </label>
                    <input
                      id="q-phone"
                      data-testid="quote-input-phone"
                      type="tel"
                      className="field"
                      placeholder="(204) 000-0000"
                      value={form.phone}
                      onChange={set("phone")}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="q-employees" className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">
                      People on site
                    </label>
                    <div className="relative">
                      <select
                        id="q-employees"
                        data-testid="quote-select-employee-count"
                        className="field appearance-none pr-10"
                        value={form.employees}
                        onChange={set("employees")}
                      >
                        {EMPLOYEES.map((o) => (
                          <option key={o} value={o} className="bg-white">
                            {o} employees
                          </option>
                        ))}
                      </select>
                      <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-slate-500">▾</span>
                    </div>
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">
                      Interested in
                    </label>
                    <div className="flex flex-wrap gap-1.5" data-testid="quote-select-service-type">
                      {SERVICES.map((s) => (
                        <button
                          key={s}
                          type="button"
                          data-testid={`quote-option-service-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                          onClick={() => setForm((f) => ({ ...f, service_type: s }))}
                          className={`rounded-full border px-3 py-1.5 text-[12px] font-semibold transition-all duration-200 ${
                            form.service_type === s
                              ? "border-[#1B365D] bg-[#1B365D] text-[#FBF7EF]"
                              : "border-black/12 text-stone-600 hover:border-black/35"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div>
                  <label htmlFor="q-message" className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">
                    Anything else?
                  </label>
                  <textarea
                    id="q-message"
                    data-testid="quote-input-message"
                    rows={3}
                    className="field resize-none"
                    placeholder="Tell us about your space, foot traffic, current vendors…"
                    value={form.message}
                    onChange={set("message")}
                  />
                </div>
                <button
                  data-testid="quote-submit-button"
                  type="submit"
                  disabled={sending}
                  className="group inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-[#1B365D] px-7 py-4 font-semibold text-[#FBF7EF] transition-all hover:-translate-y-0.5 hover:bg-[#2A4A73] hover:shadow-[0_16px_44px_rgba(27,54,93,0.35)] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {sending ? (
                    <>
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                      Sending…
                    </>
                  ) : (
                    <>
                      Request my free quote
                      <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                    </>
                  )}
                </button>
                <p className="text-center text-xs text-slate-500">
                  No spam, no obligation — just a tailored proposal within 1–3 business days.
                </p>
                <p data-testid="quote-fairplay-note" className="text-center text-xs leading-relaxed text-slate-500">
                  Standard installs are always free. If your space needs major
                  electrical or construction work (rare — mostly micro markets), we
                  flag it in the free survey and quote it separately for your approval.
                </p>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
